import { z } from 'zod';

/**
 * Zod schemas for form UX only – the backend validation is authoritative.
 * Messages in German.
 */

export const PASSWORD_MIN_LENGTH = 10;

const email = z.string().trim().min(1, 'Bitte geben Sie Ihre E-Mail-Adresse ein.').pipe(z.email('Bitte geben Sie eine gültige E-Mail-Adresse ein.'));
const newPassword = z.string().min(PASSWORD_MIN_LENGTH, `Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`);

export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'Bitte geben Sie Ihr Passwort ein.'),
  remember: z.boolean(),
});

export const twoFactorCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Bitte geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein.'),
});

export const recoveryCodeSchema = z.object({
  recovery_code: z.string().trim().min(1, 'Bitte geben Sie einen Wiederherstellungscode ein.'),
});

export const forgotPasswordSchema = z.object({ email });

export const resetPasswordSchema = z
  .object({
    email,
    password: newPassword,
    password_confirmation: z.string().min(1, 'Bitte bestätigen Sie das Passwort.'),
  })
  .refine((v) => v.password === v.password_confirmation, {
    path: ['password_confirmation'],
    message: 'Die Passwörter stimmen nicht überein.',
  });

export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Bitte geben Sie Ihr aktuelles Passwort ein.'),
    password: newPassword,
    password_confirmation: z.string().min(1, 'Bitte bestätigen Sie das neue Passwort.'),
  })
  .refine((v) => v.password === v.password_confirmation, {
    path: ['password_confirmation'],
    message: 'Die Passwörter stimmen nicht überein.',
  });

export const confirmPasswordSchema = z.object({
  password: z.string().min(1, 'Bitte geben Sie Ihr Passwort ein.'),
});

/* ---------------------------------------------------------------- Verwaltung */

const optionalText = (max, label) => z.string().trim().max(max, `${label} darf höchstens ${max} Zeichen lang sein.`).optional().or(z.literal(''));

export const CONTACT_KINDS = ['email', 'phone', 'mobile', 'fax', 'website'];

const contactSchema = z
  .object({
    kind: z.enum(CONTACT_KINDS, { message: 'Bitte wählen Sie eine Kontaktart.' }),
    value: z.string().trim().min(1, 'Bitte geben Sie einen Wert ein.').max(191, 'Höchstens 191 Zeichen.'),
    label: optionalText(50, 'Die Bezeichnung'),
    is_primary: z.boolean(),
  })
  .superRefine((contact, ctx) => {
    if (contact.kind === 'email' && contact.value && !z.email().safeParse(contact.value.trim()).success) {
      ctx.addIssue({ code: 'custom', path: ['value'], message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.' });
    }
  });

/**
 * Person/Organisation (POST/PATCH /admin/parties). Person: last_name required;
 * organization: organization_name required (mirrors PartyRequest for UX only).
 */
export const partySchema = z
  .object({
    type: z.enum(['person', 'organization']),
    salutation: optionalText(50, 'Die Anrede'),
    title: optionalText(50, 'Der Titel'),
    first_name: optionalText(120, 'Der Vorname'),
    last_name: optionalText(191, 'Der Nachname'),
    organization_name: optionalText(191, 'Der Name der Organisation'),
    letter_salutation: optionalText(191, 'Die Briefanrede'),
    birthday: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), 'Bitte geben Sie ein gültiges Datum ein.'),
    notes: optionalText(5000, 'Die Notiz'),
    contacts: z.array(contactSchema),
  })
  .superRefine((party, ctx) => {
    if (party.type === 'person' && !party.last_name?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['last_name'], message: 'Bitte geben Sie einen Nachnamen ein.' });
    }
    if (party.type === 'organization' && !party.organization_name?.trim()) {
      ctx.addIssue({ code: 'custom', path: ['organization_name'], message: 'Bitte geben Sie den Namen der Organisation ein.' });
    }
  });

/** Liegenschaft – editable fields only (name/address come from WinCasa). */
const coordinate = (max, label) =>
  z
    .string()
    .trim()
    .refine(
      (v) => v === '' || (/^-?\d{1,3}([.,]\d+)?$/.test(v) && Math.abs(Number(v.replace(',', '.'))) <= max),
      `${label}: Zahl zwischen -${max} und ${max}.`,
    );

export const propertyEditSchema = z
  .object({
    description: optionalText(5000, 'Die Beschreibung'),
    is_in_preparation: z.boolean(),
    /** G-18: Verwalter (Organisation) und Ansprechpartner – Party-ID als String, '' = keine */
    manager_party_id: z.string().optional().or(z.literal('')),
    contact_party_id: z.string().optional().or(z.literal('')),
    /** F-115: Koordinaten (Dezimalgrad, Komma oder Punkt), nur paarweise */
    latitude: coordinate(90, 'Breitengrad').optional(),
    longitude: coordinate(180, 'Längengrad').optional(),
  })
  .superRefine((v, ctx) => {
    const lat = v.latitude?.trim() ?? '';
    const lng = v.longitude?.trim() ?? '';
    if (lat !== '' && lng === '') ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Bitte auch den Längengrad angeben.' });
    if (lng !== '' && lat === '') ctx.addIssue({ code: 'custom', path: ['latitude'], message: 'Bitte auch den Breitengrad angeben.' });
  });

/** Einheit – editable fields only (master data comes from WinCasa); PATCH /admin/units/{id}. */
export const unitEditSchema = z.object({
  /** G-18 / F-120: Einheitentyp (ID als String, '' = ohne Typ) */
  unit_type_id: z.string().optional().or(z.literal('')),
  notes: optionalText(5000, 'Die Notiz'),
});

/** POST /admin/properties/{id}/roles – Person (id from the picker) and assignable role. */
export const propertyRoleAssignSchema = z.object({
  party_id: z.string().min(1, 'Bitte wählen Sie eine Person.'),
  role: z.enum(['auditor', 'meter_reader'], { message: 'Bitte wählen Sie eine Rolle.' }),
});

const userBase = {
  name: z.string().trim().min(1, 'Bitte geben Sie einen Namen ein.').max(255, 'Höchstens 255 Zeichen.'),
  email,
  roles: z.array(z.string()).min(1, 'Bitte wählen Sie mindestens eine Rolle.'),
  locale: z.enum(['de', 'en']),
  access_expires_at: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), 'Bitte geben Sie ein gültiges Datum ein.'),
  /** Verknüpfte Person/Organisation (id als String, '' = keine) */
  party_id: z.string().optional().or(z.literal('')),
};

/** POST /admin/users */
export const userCreateSchema = z.object({ ...userBase, send_invitation: z.boolean() });

/** PATCH /admin/users/{id} */
export const userUpdateSchema = z.object({ ...userBase, status: z.enum(['active', 'invited', 'locked']) });

/* ---------------------------------------------------------------- Finanzen */

const AMOUNT_PATTERN = /^(\d{1,3}(\.\d{3})+|\d+)(,\d{1,2})?$|^\d+(\.\d{1,2})?$/;
const amount = (label) =>
  z
    .string()
    .trim()
    .min(1, `Bitte geben Sie ${label} ein.`)
    .refine((v) => AMOUNT_PATTERN.test(v.replace(/\s|€/g, '')), 'Bitte geben Sie einen Betrag ≥ 0 mit höchstens zwei Nachkommastellen ein.')
    .refine((v) => (parseAmount(v) ?? 0) <= 999999999, 'Der Betrag ist zu groß.');

/**
 * Investitions-Startwerte (PUT /portal/properties/{id}/investment-baseline). Amounts are entered
 * German-style ("250.000,00" or "250000.5"); `parseAmount` converts them for the payload.
 */
export const investmentBaselineSchema = z.object({
  purchase_price: amount('den Kaufpreis'),
  initial_rent: amount('die Jahresmiete beim Kauf'),
  initial_rental_income: amount('die Mieteinnahmen beim Kauf'),
  initial_costs: amount('die Kosten beim Kauf'),
  purchase_month: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine((v) => !v || /^\d{4}-(0[1-9]|1[0-2])$/.test(v), 'Bitte geben Sie den Monat im Format JJJJ-MM ein.'),
});

/**
 * "250.000,50" | "250000,50" | "250000.50" → 250000.5 (null if not a number).
 * German input first: dots are thousands separators if a comma is present or they group
 * digits in threes ("1.000" = 1000); otherwise a single dot is the decimal separator.
 * @param {string|number|null|undefined} value
 */
export function parseAmount(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  let s = value.trim().replace(/\s|€/g, '');
  if (s === '') return null;
  if (s.includes(',') || /^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/* ---------------------------------------------------------------- Belegprüfung & Kommunikation */

const requiredText = (max, message) =>
  z
    .string()
    .trim()
    .min(1, message)
    .max(max, `Höchstens ${max.toLocaleString('de-DE')} Zeichen.`);

/** POST /admin/document-checks */
export const documentCheckCreateSchema = z.object({
  property_id: z.string().min(1, 'Bitte wählen Sie eine Liegenschaft.'),
  fiscal_year: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'Bitte geben Sie ein Abrechnungsjahr (JJJJ) ein.')
    .refine((v) => Number(v) >= 2000 && Number(v) <= 2100, 'Das Jahr muss zwischen 2000 und 2100 liegen.'),
  due_on: z.string().optional().or(z.literal('')),
});

/** Comment on a document of a Belegprüfung (admin + portal). */
export const commentSchema = z.object({ body: requiredText(5000, 'Bitte geben Sie einen Kommentar ein.') });

/** POST/PATCH /admin/announcements – property_ids as strings (checkboxes). */
export const announcementSchema = z.object({
  title: requiredText(500, 'Bitte geben Sie einen Titel ein.'),
  body_html: z.string().max(500000, 'Der Text ist zu lang.'),
  property_ids: z.array(z.string()).min(1, 'Bitte wählen Sie mindestens eine Liegenschaft.'),
  is_published: z.boolean(),
  publish_at: z.string().optional().or(z.literal('')),
  unpublish_at: z.string().optional().or(z.literal('')),
});

/** Reply / follow-up in a message thread (admin + portal). */
export const messageReplySchema = z.object({ body: requiredText(10000, 'Bitte geben Sie eine Nachricht ein.') });

/** POST /portal/messages */
export const messageCreateSchema = z.object({
  unit_id: z.string().min(1, 'Bitte wählen Sie eine Einheit.'),
  subject: requiredText(300, 'Bitte geben Sie einen Betreff ein.'),
  body: requiredText(10000, 'Bitte geben Sie eine Nachricht ein.'),
});

/** PUT /admin/mail-templates/{key} */
export const mailTemplateSchema = z.object({
  subject: requiredText(500, 'Bitte geben Sie einen Betreff ein.'),
  body_html: requiredText(200000, 'Bitte geben Sie einen Text ein.'),
});

/* ---------------------------------------------------------------- Zähler, Vollmachten, Links */

const METER_VALUE_PATTERN = /^\d+([.,]\d+)?$/;

/**
 * Meter value as typed ("12345,6" or "12345.6") → number (null if empty/invalid). No thousands separators:
 * meter readings are entered as plain digits.
 * @param {string|number|null|undefined} value
 */
export function parseMeterValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = value.trim().replace(/\s/g, '');
  if (!METER_VALUE_PATTERN.test(s)) return null;
  return Number(s.replace(',', '.'));
}

/** Zählerstand (Portal-Erfassung). */
export const meterReadingSchema = z.object({
  value: z
    .string()
    .trim()
    .min(1, 'Bitte geben Sie den Zählerstand ein.')
    .refine((v) => METER_VALUE_PATTERN.test(v.replace(/\s/g, '')), 'Bitte nur Ziffern und höchstens ein Komma eingeben.'),
  is_new_meter: z.boolean(),
});

/** POST/PATCH /admin/meters – ids as strings (selects). */
export const meterSchema = z.object({
  property_id: z.string().min(1, 'Bitte wählen Sie eine Liegenschaft.'),
  number: requiredText(255, 'Bitte geben Sie die Zählernummer ein.'),
  type: z.string().trim().max(255, 'Höchstens 255 Zeichen.'),
  contract_number: z.string().trim().max(255, 'Höchstens 255 Zeichen.'),
  supplier_id: z.string(),
  reader_party_id: z.string(),
  description: z.string().max(5000, 'Höchstens 5.000 Zeichen.'),
});

/** Versorger / Link-Kategorie: nur ein Name. */
export const nameSchema = z.object({ name: requiredText(191, 'Bitte geben Sie einen Namen ein.') });

/** Einzelmail an einen Ableser. */
export const readerMailSchema = z.object({
  subject: requiredText(500, 'Bitte geben Sie einen Betreff ein.'),
  body_html: requiredText(200000, 'Bitte geben Sie einen Text ein.'),
});

/** POST/PATCH /admin/links */
export const linkSchema = z
  .object({
    name: requiredText(191, 'Bitte geben Sie einen Namen ein.'),
    url: z
      .string()
      .trim()
      .min(1, 'Bitte geben Sie eine Adresse ein.')
      .max(2000, 'Höchstens 2.000 Zeichen.')
      .refine((v) => /^https?:\/\/\S+$/i.test(v), 'Bitte geben Sie eine vollständige Adresse mit http:// oder https:// ein.'),
    description: z.string().max(5000, 'Höchstens 5.000 Zeichen.'),
    link_category_id: z.string(),
    is_active: z.boolean(),
    /** F-1005: Zielgruppe und Hervorhebung auf der Portal-Startseite */
    is_highlighted: z.boolean().optional(),
    audience: z.enum(['all', 'roles', 'users']).optional(),
    roles: z.array(z.string()).optional(),
    user_ids: z.array(z.string()).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.audience === 'roles' && !(v.roles?.length > 0)) ctx.addIssue({ code: 'custom', path: ['roles'], message: 'Bitte mindestens eine Rolle wählen.' });
    if (v.audience === 'users' && !(v.user_ids?.length > 0))
      ctx.addIssue({ code: 'custom', path: ['user_ids'], message: 'Bitte mindestens einen Benutzer wählen.' });
  });

/** POST /portal/proxy-authorizations (signature is checked separately – canvas). */
export const proxyAuthorizationSchema = z
  .object({
    property_id: z.string().min(1, 'Bitte wählen Sie die Liegenschaft.'),
    principal_name: requiredText(191, 'Bitte geben Sie Ihren Namen ein.'),
    principal_address: requiredText(191, 'Bitte geben Sie Ihre Anschrift ein.'),
    attorney_type: z.enum(['name', 'administrator', 'co_owner'], { message: 'Bitte wählen Sie, wer Sie vertreten soll.' }),
    attorney_name: z.string().trim().max(191, 'Höchstens 191 Zeichen.'),
    weg_name: z.string().trim().max(191, 'Höchstens 191 Zeichen.'),
    weg_location: z.string().trim().max(191, 'Höchstens 191 Zeichen.'),
    meeting_date: z.string().min(1, 'Bitte geben Sie das Datum der Versammlung ein.'),
    instructions: z.string().max(10000, 'Höchstens 10.000 Zeichen.'),
  })
  .refine((v) => v.attorney_type === 'administrator' || v.attorney_name.trim() !== '', {
    path: ['attorney_name'],
    message: 'Bitte geben Sie den Namen der bevollmächtigten Person ein.',
  });

/** POST …/proxy-authorizations/{id}/confirm */
export const proxyCodeSchema = z.object({ code: requiredText(20, 'Bitte geben Sie den Bestätigungscode ein.') });

/* ---------------------------------------------------------------- Einstellungen / Branding (F-014/F-015) */

/** Leer = Standard wiederherstellen. */
const optionalEmail = z
  .string()
  .trim()
  .max(191, 'Höchstens 191 Zeichen.')
  .refine((v) => v === '' || z.email().safeParse(v).success, 'Bitte geben Sie eine gültige E-Mail-Adresse ein.');
const optionalUrl = z
  .string()
  .trim()
  .max(500, 'Höchstens 500 Zeichen.')
  .refine((v) => v === '' || /^https?:\/\/\S+$/i.test(v), 'Bitte geben Sie eine vollständige Adresse mit https:// ein.');
const noMarkup = (max) =>
  z
    .string()
    .trim()
    .max(max, `Höchstens ${max} Zeichen.`)
    .refine((v) => !/[<>]/.test(v), 'Die Zeichen < und > sind nicht erlaubt.');

/** Einstellungen → Branding (Texte/Farbe; Bilder separat). */
export const settingsBrandingSchema = z.object({
  company_name: noMarkup(120),
  short_name: noMarkup(40),
  primary_color: z
    .string()
    .trim()
    .refine((v) => v === '' || /^#[0-9a-fA-F]{6}$/.test(v), 'Bitte als Hex-Farbe angeben, z. B. #f38416.'),
  welcome_text: z.string().max(1000, 'Höchstens 1.000 Zeichen.'),
  imprint_url: optionalUrl,
  privacy_url: optionalUrl,
  support_phone: z
    .string()
    .trim()
    .refine((v) => v === '' || /^\+?[0-9 ()/.-]{3,40}$/.test(v), 'Bitte nur Ziffern, Leerzeichen und + ( ) / . - verwenden.'),
  support_email: optionalEmail,
});

/** Einstellungen → E-Mail. */
export const settingsEmailSchema = z.object({
  sender_name: z
    .string()
    .trim()
    .max(100, 'Höchstens 100 Zeichen.')
    .refine((v) => !/[<>"@\n\r]/.test(v), 'Der Absendername darf keine Zeichen wie < > " @ enthalten.'),
  reply_to: optionalEmail,
  signature_html: z.string().max(20000, 'Höchstens 20.000 Zeichen.'),
});

/** Einstellungen → Funktionspostfächer. */
export const settingsMailboxesSchema = z.object({ management: optionalEmail, ops: optionalEmail });
